/**
 * Bets: payouts, canonical targets, and the pockets each bet covers.
 *
 * Wheel-sector call bets (Voisins, Tiers, Orphelins, Jeu zéro, Neighbours) are
 * *presets*: placing one inserts its constituent standard bets. Their pocket
 * memberships are derived from the ring arcs in `wheels.ts`, never hardcoded —
 * the hardcoded part is only the canonical chip layout on the felt, and the
 * tests assert that layout covers exactly the derived arc.
 */

import type { BetType, PlacedBet, PocketId, WheelDefinition, WheelStyle } from './types';
import {
  basketPockets,
  columnPockets,
  dozenPockets,
  legalCorners,
  legalSixLines,
  legalSplits,
  legalStreets,
} from './felt';
import { arc, getWheel } from './wheels';

// ---------------------------------------------------------------------------
// Payouts
// ---------------------------------------------------------------------------

/** Payout odds to 1. A winning bet returns `chips * payout` in profit. */
export const PAYOUTS: Readonly<Record<BetType, number>> = {
  straight: 35,
  split: 17,
  street: 11,
  corner: 8,
  'six-line': 5,
  basket: 6,
  column: 2,
  dozen: 2,
  red: 1,
  black: 1,
  odd: 1,
  even: 1,
  low: 1,
  high: 1,
};

export const BET_TYPE_LABELS: Readonly<Record<BetType, string>> = {
  straight: 'Straight',
  split: 'Split',
  street: 'Street',
  corner: 'Corner',
  'six-line': 'Six line',
  basket: 'Basket',
  column: 'Column',
  dozen: 'Dozen',
  red: 'Red',
  black: 'Black',
  odd: 'Odd',
  even: 'Even',
  low: 'Low (1–18)',
  high: 'High (19–36)',
};

export function payoutOf(betType: BetType): number {
  return PAYOUTS[betType];
}

/** Bet types whose target is a `|`-joined pocket list. */
const POCKET_SET_BETS: ReadonlySet<BetType> = new Set<BetType>([
  'straight', 'split', 'street', 'corner', 'six-line', 'basket',
]);

const EVEN_MONEY_BETS: ReadonlySet<BetType> = new Set<BetType>([
  'red', 'black', 'odd', 'even', 'low', 'high',
]);

export function isEvenMoneyBet(betType: BetType): boolean {
  return EVEN_MONEY_BETS.has(betType);
}

// ---------------------------------------------------------------------------
// Canonical targets
// ---------------------------------------------------------------------------

function pocketRank(pocketId: PocketId): number {
  if (pocketId === '0') return -2;
  if (pocketId === '00') return -1;
  return Number(pocketId);
}

export function comparePockets(a: PocketId, b: PocketId): number {
  return pocketRank(a) - pocketRank(b);
}

/** Canonical `|`-joined target for a pocket-set bet. Order-independent. */
export function encodeTarget(pockets: readonly PocketId[]): string {
  return [...pockets].sort(comparePockets).join('|');
}

export function decodeTarget(target: string): PocketId[] {
  if (target === '') return [];
  return target.split('|') as PocketId[];
}

/** Stable identity for a bet, so repeat placements merge instead of stacking. */
export function betKey(bet: Pick<PlacedBet, 'betType' | 'target'>): string {
  return `${bet.betType}:${bet.target}`;
}

export function makeBet(betType: BetType, pockets: readonly PocketId[], chips: number): PlacedBet {
  return { betType, target: encodeTarget(pockets), chips };
}

// ---------------------------------------------------------------------------
// Coverage
// ---------------------------------------------------------------------------

/**
 * Pockets a bet covers on a given wheel. Pockets not present on that wheel are
 * dropped, so a malformed bet degrades rather than throwing.
 */
export function coveredPockets(bet: PlacedBet, wheel: WheelDefinition): PocketId[] {
  const raw = rawCoveredPockets(bet, wheel.style);
  return raw.filter((pocketId) => wheel.pockets.has(pocketId));
}

function rawCoveredPockets(bet: PlacedBet, style: WheelStyle): PocketId[] {
  const { betType, target } = bet;

  if (POCKET_SET_BETS.has(betType)) return decodeTarget(target);

  switch (betType) {
    case 'column':
      return columnPockets(Number(target));
    case 'dozen':
      return dozenPockets(Number(target));
    case 'red':
    case 'black':
    case 'odd':
    case 'even':
    case 'low':
    case 'high': {
      const wheel = getWheel(style);
      return wheel.ordered
        .filter((pocket) => {
          switch (betType) {
            case 'red':
              return pocket.colour === 'red';
            case 'black':
              return pocket.colour === 'black';
            case 'odd':
              return pocket.parity === 'odd';
            case 'even':
              return pocket.parity === 'even';
            case 'low':
              return pocket.highLow === 'low';
            case 'high':
              return pocket.highLow === 'high';
          }
        })
        .map((pocket) => pocket.id);
    }
    default:
      return [];
  }
}

/** How many pockets a bet type covers, for validation. `null` when variable. */
export function expectedCoverCount(betType: BetType): number | null {
  switch (betType) {
    case 'straight': return 1;
    case 'split': return 2;
    case 'street': return 3;
    case 'corner': return 4;
    case 'six-line': return 6;
    case 'basket': return 5;
    case 'column':
    case 'dozen': return 12;
    default: return isEvenMoneyBet(betType) ? 18 : null;
  }
}

// ---------------------------------------------------------------------------
// Legal bets and validation
// ---------------------------------------------------------------------------

/** Every legal pocket-set bet of a type on a style's felt, as pocket lists. */
export function legalPocketSets(betType: BetType, style: WheelStyle): PocketId[][] {
  switch (betType) {
    case 'straight':
      return (style === 'single-zero' ? ['0'] : ['0', '00'])
        .concat(Array.from({ length: 36 }, (_, i) => String(i + 1)))
        .map((pocketId) => [pocketId as PocketId]);
    case 'split':
      return legalSplits(style);
    case 'street':
      return legalStreets(style);
    case 'corner':
      return legalCorners(style);
    case 'six-line':
      return legalSixLines();
    case 'basket': {
      const basket = basketPockets(style);
      return basket ? [basket] : [];
    }
    default:
      return [];
  }
}

const legalTargetCache = new Map<string, ReadonlySet<string>>();

function legalTargets(betType: BetType, style: WheelStyle): ReadonlySet<string> {
  const cacheKey = `${betType}:${style}`;
  let set = legalTargetCache.get(cacheKey);
  if (!set) {
    set = new Set(legalPocketSets(betType, style).map(encodeTarget));
    legalTargetCache.set(cacheKey, set);
  }
  return set;
}

export type BetValidation = { ok: true } | { ok: false; reason: string };

export function validateBet(bet: PlacedBet, style: WheelStyle): BetValidation {
  if (!Number.isInteger(bet.chips)) return { ok: false, reason: 'Chips must be a whole number' };
  if (bet.chips <= 0) return { ok: false, reason: 'Chips must be positive' };

  if (POCKET_SET_BETS.has(bet.betType)) {
    if (bet.betType === 'basket' && style === 'single-zero') {
      return { ok: false, reason: 'The basket exists on the double-zero felt only' };
    }
    if (!legalTargets(bet.betType, style).has(bet.target)) {
      return {
        ok: false,
        reason: `${BET_TYPE_LABELS[bet.betType]} ${bet.target} is not a legal bet on the ${style} felt`,
      };
    }
    return { ok: true };
  }

  if (bet.betType === 'column' || bet.betType === 'dozen') {
    return ['1', '2', '3'].includes(bet.target)
      ? { ok: true }
      : { ok: false, reason: `${BET_TYPE_LABELS[bet.betType]} target must be 1, 2 or 3` };
  }

  if (isEvenMoneyBet(bet.betType)) {
    return bet.target === ''
      ? { ok: true }
      : { ok: false, reason: 'Even-money bets take an empty target' };
  }

  return { ok: false, reason: `Unknown bet type '${bet.betType}'` };
}

export function isValidBet(bet: PlacedBet, style: WheelStyle): boolean {
  return validateBet(bet, style).ok;
}

/** Human-readable description, used in the felt list, library and casino mode. */
export function betLabel(bet: PlacedBet): string {
  const base = BET_TYPE_LABELS[bet.betType];
  if (POCKET_SET_BETS.has(bet.betType)) {
    return `${base} ${decodeTarget(bet.target).join('/')}`;
  }
  if (bet.betType === 'column' || bet.betType === 'dozen') {
    return `${base} ${bet.target}`;
  }
  return base;
}

// ---------------------------------------------------------------------------
// Wheel-sector call bets (single-zero)
// ---------------------------------------------------------------------------

export type SectorId = 'voisins' | 'tiers' | 'orphelins' | 'jeu-zero';

export interface SectorPreset {
  readonly id: SectorId;
  readonly name: string;
  readonly shortName: string;
  /** Ring arcs, inclusive, that define the sector. The authoritative definition. */
  readonly arcs: readonly (readonly [PocketId, PocketId])[];
  /** Canonical chip placement on the felt. Must cover exactly the arc pockets. */
  readonly layout: readonly PlacedBet[];
  readonly chips: number;
  readonly pocketCount: number;
}

function sector(
  sectorId: SectorId,
  name: string,
  shortName: string,
  arcs: readonly (readonly [PocketId, PocketId])[],
  layout: readonly PlacedBet[],
): SectorPreset {
  return {
    id: sectorId,
    name,
    shortName,
    arcs,
    layout,
    chips: layout.reduce((sum, bet) => sum + bet.chips, 0),
    pocketCount: new Set(layout.flatMap((bet) => decodeTarget(bet.target))).size,
  };
}

/**
 * The four French call bets. Single-zero only — their whole meaning is a
 * contiguous arc of the European ring, which has no counterpart on the
 * American wheel.
 */
export const SECTOR_PRESETS: readonly SectorPreset[] = [
  sector('voisins', 'Voisins du zéro', 'Voisins', [['22', '25']], [
    makeBet('street', ['0', '2', '3'], 2),
    makeBet('split', ['4', '7'], 1),
    makeBet('split', ['12', '15'], 1),
    makeBet('split', ['18', '21'], 1),
    makeBet('split', ['19', '22'], 1),
    makeBet('corner', ['25', '26', '28', '29'], 2),
    makeBet('split', ['32', '35'], 1),
  ]),
  sector('tiers', 'Tiers du cylindre', 'Tiers', [['27', '33']], [
    makeBet('split', ['5', '8'], 1),
    makeBet('split', ['10', '11'], 1),
    makeBet('split', ['13', '16'], 1),
    makeBet('split', ['23', '24'], 1),
    makeBet('split', ['27', '30'], 1),
    makeBet('split', ['33', '36'], 1),
  ]),
  sector('orphelins', 'Orphelins', 'Orphelins', [['17', '6'], ['1', '9']], [
    makeBet('straight', ['1'], 1),
    makeBet('split', ['6', '9'], 1),
    makeBet('split', ['14', '17'], 1),
    makeBet('split', ['17', '20'], 1),
    makeBet('split', ['31', '34'], 1),
  ]),
  sector('jeu-zero', 'Jeu zéro', 'Jeu 0', [['12', '15']], [
    makeBet('straight', ['26'], 1),
    makeBet('split', ['0', '3'], 1),
    makeBet('split', ['12', '15'], 1),
    makeBet('split', ['32', '35'], 1),
  ]),
];

export const SECTORS_BY_ID: Readonly<Record<SectorId, SectorPreset>> = Object.fromEntries(
  SECTOR_PRESETS.map((preset) => [preset.id, preset]),
) as Record<SectorId, SectorPreset>;

/** Pockets in a sector, derived from the wheel sequence via its ring arcs. */
export function sectorPockets(preset: SectorPreset): PocketId[] {
  const wheel = getWheel('single-zero');
  return preset.arcs.flatMap(([from, to]) => arc(wheel, from, to));
}

/** Scale a preset's canonical layout by a whole-chip unit. */
export function sectorBets(preset: SectorPreset, unit = 1): PlacedBet[] {
  return preset.layout.map((bet) => ({ ...bet, chips: bet.chips * unit }));
}

/**
 * Which sector presets are fully present in a set of bets, and at what unit.
 * Lets the UI name a sector without storing preset identity in the spread.
 */
export function identifySectors(bets: readonly PlacedBet[]): { preset: SectorPreset; unit: number }[] {
  const staked = new Map<string, number>();
  for (const bet of bets) staked.set(betKey(bet), (staked.get(betKey(bet)) ?? 0) + bet.chips);

  const found: { preset: SectorPreset; unit: number }[] = [];
  for (const preset of SECTOR_PRESETS) {
    const units = preset.layout.map((bet) => {
      const placed = staked.get(betKey(bet)) ?? 0;
      return placed > 0 && placed % bet.chips === 0 ? placed / bet.chips : 0;
    });
    const unit = Math.min(...units);
    if (unit > 0) found.push({ preset, unit });
  }
  return found;
}
